import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Button,
  Grid,
  IconButton,
  CircularProgress
} from '@mui/material';
import {
  ArrowBackIos as ArrowBackIosIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  Category as CategoryIcon
} from '@mui/icons-material';
import { API_BASE_URL, authenticatedFetch } from "../config/api";
import apiTokenService from '../services/apiTokenService';
import FastImage from './FastImage';
import PlaceholderImage from './PlaceholderImage';
import { preloadProductImages, preloadCategoryImages } from '../utils/imagePreloader';
import layoutOptimizer from '../utils/layoutOptimizer';

const Home = () => {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [offers, setOffers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [productsByBrand, setProductsByBrand] = useState({});
  const [banners, setBanners] = useState([]);
  const [bannersLoading, setBannersLoading] = useState(true);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [categoriesLoading, setCategoriesLoading] = useState(false); // Changed to false for instant display
  const [productsLoading, setProductsLoading] = useState(false); // Changed to false for instant display
  const [offersLoading, setOffersLoading] = useState(true); // Start with true for offers
  
  const navigate = useNavigate();

  // Optimized mouse drag scroll to prevent forced synchronous layout
  const handleMouseDown = (e) => {
    const container = e.currentTarget;
    let startX, startScrollLeft;
    
    // Batch the initial measurements
    layoutOptimizer.batchMeasurements([container], ([measurement]) => {
      startX = e.pageX - measurement.rect.left;
      startScrollLeft = measurement.scrollLeft;
    });

    const handleMouseMove = (e) => {
      e.preventDefault();
      
      // Use the cached measurements to avoid forced layout
      if (startX !== undefined && startScrollLeft !== undefined) {
        const x = e.pageX - container.getBoundingClientRect().left;
        const walk = (x - startX) * 2; // Scroll speed multiplier
        
        // Use optimized scrolling
        const targetScroll = startScrollLeft - walk;
        layoutOptimizer.scheduleLayoutWork(() => {
          container.scrollLeft = targetScroll;
        });
      }
    };

    const handleMouseUp = () => {
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('mouseleave', handleMouseUp);
    };

    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('mouseleave', handleMouseUp);
  };

  // Shuffle array function
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Load banners from API
  useEffect(() => {
    const loadBanners = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/banners.php`);
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setBanners(data.banners || []);
          }
        }
      } catch (error) {
        console.error('Error loading banners:', error);
      } finally {
        setBannersLoading(false);
      }
    };

    loadBanners();
  }, []);

  // Auto-rotate banners every 5 seconds
  useEffect(() => {
    if (banners.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentBannerIndex((prev) => (prev + 1) % banners.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [banners.length]);

  // Load categories from API
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/categories.php`);
        if (response.ok) {
          const data = await response.json();
          // Shuffle categories randomly on each load
          const shuffledCategories = shuffleArray(data || []);
          setCategories(shuffledCategories);
          
          // Preload category images for better performance
          preloadCategoryImages(shuffledCategories.slice(0, 8), { maxConcurrent: 3 });
        }
      } catch (error) {
        // Error loading categories - handle silently
        console.error('Error loading categories:', error);
      }
      // No finally block needed - we start with loading=false
    };

    loadCategories();
  }, []);

  // Load offers from API (products with discount_price > 0)
  useEffect(() => {
    const loadOffers = async () => {
      try {
        console.log('Home: Fetching offers with token...');
        const response = await apiTokenService.fetch(`${API_BASE_URL}/api/offers.php?limit=20`);
        console.log('Home: Offers response status:', response.status);
        
        if (response.ok) {
          const data = await response.json();
          
          if (data.success) {
            const offersData = data.products || [];
            setOffers(offersData);
          } else {
            setOffers([]);
          }
        } else {
          setOffers([]);
        }
      } catch (error) {
        console.error('Error loading offers:', error);
        setOffers([]);
      } finally {
        setOffersLoading(false);
      }
    };

    loadOffers();
  }, []);

  // Load random products from API
  useEffect(() => {
    const loadProducts = async () => {
      try {
        // First, try to get a large sample of products
        const response = await apiTokenService.fetch(`${API_BASE_URL}/api/products.php?per_page=all`);
        if (response.ok) {
          const data = await response.json();
          let products = data.products || [];
          
          // Filter out inactive products first
          products = products.filter(product => 
            product.active === 1 || product.active === true || product.status === 'active'
          );
          
          // If we have more than 200 products, let's create a diverse sample
          if (products.length > 200) {
            // Group all products by brand first
            const allProductsByBrand = {};
            products.forEach(product => {
              const brandId = product.brand_id || 'unknown';
              if (!allProductsByBrand[brandId]) {
                allProductsByBrand[brandId] = [];
              }
              allProductsByBrand[brandId].push(product);
            });
            
            // Take up to 8 products from each brand to ensure diversity
            const diverseProducts = [];
            Object.values(allProductsByBrand).forEach(brandProducts => {
              const shuffled = brandProducts.sort(() => 0.5 - Math.random());
              diverseProducts.push(...shuffled.slice(0, 8));
            });
            
            products = diverseProducts.slice(0, 300); // Final limit
          }
          
          setProducts(products);
          
          // Preload first batch of product images for better performance
          const firstBatchProducts = products.slice(0, 20); // First 20 products
          preloadProductImages(firstBatchProducts, { maxConcurrent: 5 });
          
          // Group products by brand
          const grouped = {};
          
          // First, initialize all brands from the brands array
          brands.forEach(brand => {
            grouped[brand.id] = {
              brandName: brand.name,
              products: []
            };
          });
          
          // Then add products to their respective brands
          products.forEach(product => {
            const brandId = product.brand_id;
            if (brandId && grouped[brandId]) {
              grouped[brandId].products.push(product);
            }
          });
          
          setProductsByBrand(grouped);
        }
      } catch (error) {
        // Error loading products - handle silently
        console.error('Error loading products:', error);
      }
      // No finally block needed - we start with loading=false
    };

    // Only load products after brands have been loaded
    if (brands.length > 0) {
      loadProducts();
    }
  }, [brands]);

  // Load brands for product display
  useEffect(() => {
    const loadBrands = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/brands.php`);
        if (response.ok) {
          const data = await response.json();
          setBrands(data || []);
        }
      } catch (error) {
        // Error loading brands - handle silently
      }
    };

    loadBrands();
  }, []);

  // Price calculation function
  const getPrice = (product) => {
    if (product.after_discount && Number(product.after_discount) > 0) {
      return Number(product.after_discount);
    }
    if (product.discount_price && Number(product.discount_price) > 0) {
      return Number(product.discount_price);
    }
    if (product.price && Number(product.price) > 0) {
      return Number(product.price);
    }
    return Number(product.sell_price || 0);
  };

  // Handle banner navigation
  const handleBannerClick = (link) => {
    if (link) {
      if (link.startsWith('http')) {
        window.open(link, '_blank');
      } else {
        navigate(link);
      }
    }
  };

  const nextBanner = () => {
    setCurrentBannerIndex((prev) => (prev + 1) % banners.length);
  };

  const prevBanner = () => {
    setCurrentBannerIndex((prev) => (prev - 1 + banners.length) % banners.length);
  };

  // Handle category navigation (FIXED LINE)
  const handleCategoryClick = (categoryId) => {
    navigate(`/store?category=${categoryId}`);
  };

  // Handle brand navigation
  const handleBrandClick = (brandId) => {
    // Ensure we pass a valid brand ID, not 'unknown'
    if (brandId && brandId !== 'unknown') {
      navigate(`/store?brand=${brandId}`);
    } else {
      // Invalid brand ID - handle silently
    }
  };

  // Render category item
  const renderCategoryItem = (category, index) => (
    <Box
      key={category.id || index}
      onClick={() => handleCategoryClick(category.id)}
      sx={{
        minWidth: 100,
        maxWidth: 100,
        textAlign: 'center',
        cursor: 'pointer',
        p: 1,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-5px)'
        }
      }}
    >
      <Box
        sx={{
          width: 80,
          height: 80,
          mx: 'auto',
          mb: 1,
          backgroundColor: category.image ? 'transparent' : '#3b5dac',
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(59, 93, 172, 0.3)',
          transition: 'all 0.3s ease',
          overflow: 'hidden',
          '&:hover': {
            backgroundColor: category.image ? 'transparent' : '#2a406e',
            boxShadow: '0 6px 20px rgba(59, 93, 172, 0.4)'
          }
        }}
      >
        {category.image ? (
          <FastImage
            src={category.image.startsWith('http') ? 
              category.image : 
              `https://al-sadik.com/api/uploads/categories/${category.image}`
            }
            alt={category.name}
            priority={index < 4} // First 4 categories get priority loading
            aspectRatio="1"
            sx={{
              width: '100%',
              height: '100%',
              borderRadius: 2
            }}
          />
        ) : (
          <CategoryIcon sx={{ 
            fontSize: 40, 
            color: 'white'
          }} />
        )}
      </Box>
      <Typography
        variant="caption"
        sx={{
          fontSize: '0.75rem',
          fontWeight: 'bold',
          color: 'text.primary',
          display: 'block',
          lineHeight: 1.2,
          height: '2.4em',
          overflow: 'hidden'
        }}
      >
        {category.name}
      </Typography>
    </Box>
  );

  // Render product card (similar to Store.js format) for brand strips
  const renderProductCard = (product, index = 0) => (
    <Box
      key={product.id}
      sx={{
        minWidth: 150,
        maxWidth: 150,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        background: 'transparent',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        mr: 2,
        '&:hover': {
          transform: 'translateY(-4px)'
        }
      }}
      onClick={() => navigate(`/product/${product.id}`)}
    >
      <FastImage
        src={product.image && product.image.trim() ? 
          (product.image.startsWith('http') ? 
            product.image : 
            `https://al-sadik.com/api/uploads/products/${product.image.replace('uploads/', '')}`
          ) : '/placeholder-image.jpg'
        }
        alt={product.name}
        priority={index < 6} // First 6 products get priority loading
        aspectRatio="1"
        sx={{
          width: '100%',
          borderRadius: 1,
          mb: 1
        }}
      />
      <Box sx={{ width: '100%', textAlign: 'center', px: 1 }}>
        {/* Product name */}
        <Typography variant="h6" sx={{
          fontWeight: 'bold',
          fontSize: '0.8rem',
          mb: 0.5,
          height: '2.2em',
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          lineHeight: 1.1,
          color: 'grey.700'
        }}>
          {product.name}
        </Typography>
        
        {/* Price */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 'bold',
            fontSize: '0.9rem',
            color: 'text.primary',
            mb: 1
          }}
        >
          {getPrice(product).toLocaleString()} د.ع
        </Typography>
      </Box>
    </Box>
  );

  // Render offer card (with discount styling)
  const renderOfferCard = (product, index = 0, brandIndex = 0) => {
    // Use the correct price structure from the database
    const originalPrice = Number(product.sell_price || 0); // Original selling price
    const finalPrice = Number(product.after_discount || 0); // Final price after discount
    const discountAmount = Number(product.discount_price || 0); // Discount amount in IQD
    const discountPercentage = product.discount_percentage || (originalPrice > 0 ? Math.round((discountAmount / originalPrice) * 100) : 0);
    const savings = discountAmount; // Discount amount is the savings

    return (
      <Box
        key={product.id}
        sx={{
          minWidth: 160,
          maxWidth: 160,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          background: 'white',
          borderRadius: 2,
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          mr: 2,
          p: 1,
          position: 'relative',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          border: '1px solid #f0f0f0',
          '&:hover': {
            transform: 'translateY(-6px) scale(1.02)',
            boxShadow: '0 8px 20px rgba(0, 0, 0, 0.15)'
          }
        }}
        onClick={() => navigate(`/product/${product.id}`)}
      >
        {/* Discount Badge */}
        {discountPercentage > 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              background: '#ff4444',
              color: 'white',
              borderRadius: '50%',
              width: 35,
              height: 35,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.7rem',
              fontWeight: 'bold',
              zIndex: 2,
              boxShadow: '0 2px 8px rgba(255, 68, 68, 0.3)'
            }}
          >
            -{discountPercentage}%
          </Box>
        )}

        <FastImage
          src={product.image && product.image.trim() ? 
            (product.image.startsWith('http') ? 
              product.image : 
              `https://al-sadik.com/api/uploads/products/${product.image.replace('uploads/', '')}`
            ) : '/placeholder-image.jpg'
          }
          alt={product.name}
          priority={brandIndex < 2 && index < 5} // First 2 brands, first 5 products get priority
          aspectRatio="1"
          sx={{
            width: '100%',
            borderRadius: 1,
            mb: 1
          }}
        />
        
        <Box sx={{ width: '100%', textAlign: 'center', px: 1 }}>
          {/* Product name */}
          <Typography variant="h6" sx={{
            fontWeight: 'bold',
            fontSize: '0.8rem',
            mb: 0.5,
            height: '2.2em',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            lineHeight: 1.1,
            color: '#333'
          }}>
            {product.name}
          </Typography>
          
          {/* Prices */}
          <Box sx={{ mb: 1 }}>
            {/* Original price (crossed out) */}
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.7rem',
                color: '#999',
                textDecoration: 'line-through',
                mb: 0.2
              }}
            >
              {originalPrice.toLocaleString()} د.ع
            </Typography>
            
            {/* Final price after discount */}
            <Typography
              variant="h6"
              sx={{
                fontWeight: 'bold',
                fontSize: '0.9rem',
                color: '#ff4444',
                textShadow: 'none'
              }}
            >
              {finalPrice.toLocaleString()} د.ع
            </Typography>
            
            {/* Savings amount */}
            {savings > 0 && (
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.65rem',
                  color: '#ff4444',
                  fontWeight: 'bold',
                  mt: 0.2
                }}
              >
                وفر {savings.toLocaleString()} د.ع
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  // Render brand strip
  const renderBrandStrip = (brandId, brandData, brandIndex = 0) => (
    <Box key={brandId} sx={{ mb: 4 }}>
      {/* Brand Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, px: 2 }}>
        <Typography 
          variant="h4" 
          onClick={() => handleBrandClick(brandId)}
          sx={{ 
            fontWeight: 'bold', 
            color: '#3b5dac',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            fontSize: '1.8rem',
            '&:hover': {
              color: '#2a406e',
              textDecoration: 'underline'
            }
          }}
        >
          {brandData.brandName}
        </Typography>
      </Box>

      {/* Products Strip */}
      <Box
        id={`brand-products-${brandId}`}
        onMouseDown={handleMouseDown}
        sx={{
          display: 'flex',
          gap: 0,
          overflowX: 'auto',
          pb: 2,
          px: 2,
          scrollBehavior: 'smooth',
          cursor: 'grab',
          userSelect: 'none',
          '&:active': {
            cursor: 'grabbing'
          },
          '&::-webkit-scrollbar': { display: 'none' },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none'
        }}
      >
        {brandData.products.slice(0, 8).map((product, index) => (
          <Box
            key={product.id}
            sx={{
              minWidth: 150,
              maxWidth: 150,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              background: 'transparent',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              mr: 2,
              '&:hover': {
                transform: 'translateY(-4px)'
              }
            }}
            onClick={() => navigate(`/product/${product.id}`)}
          >
            <FastImage
              src={product.image && product.image.trim() ? 
                (product.image.startsWith('http') ? 
                  product.image : 
                  `https://al-sadik.com/api/uploads/products/${product.image.replace('uploads/', '')}`
                ) : '/placeholder-image.jpg'
              }
              alt={product.name}
              priority={brandIndex < 2 && index < 8} // First 2 brands, first 8 products get priority
              aspectRatio="1"
              sx={{
                width: '100%',
                borderRadius: 1,
                mb: 1
              }}
            />
            <Box sx={{ width: '100%', textAlign: 'center', px: 1 }}>
              {/* Product name */}
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 'bold',
                  fontSize: '0.85rem',
                  color: 'text.primary',
                  mb: 0.5,
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  minHeight: '2.4em'
                }}
              >
                {product.name}
              </Typography>
              
              {/* Product price */}
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 'bold',
                  fontSize: '0.9rem',
                  color: 'text.primary',
                  mb: 1
                }}
              >
                {getPrice(product).toLocaleString()} د.ع
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );

  // Generate JSON-LD structured data for the home page
  const generateHomeJsonLd = () => {
    return {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "Al Sadik Store",
      "description": "متجر الصادق - منتجات متنوعة بأفضل الأسعار",
      "url": window.location.origin,
      "potentialAction": {
        "@type": "SearchAction",
        "target": `${window.location.origin}/store?search={search_term_string}`,
        "query-input": "required name=search_term_string"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Al Sadik Store"
      }
    };
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      {/* JSON-LD Structured Data for Home Page */}
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateHomeJsonLd())
        }}
      />
      
      {/* Categories Section */}
      <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #eee', mb: 0, pt: 8 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, px: 2, pt: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#3b5dac' }}>
            تصفح الفئات
          </Typography>
        </Box>

        <Box
          id="categories-container"
          onMouseDown={handleMouseDown}
          sx={{
            display: 'flex',
            gap: 2,
            overflowX: 'auto',
            pb: 2,
            px: 2,
            scrollBehavior: 'smooth',
            cursor: 'grab',
            userSelect: 'none',
            '&:active': {
              cursor: 'grabbing'
            },
            '&::-webkit-scrollbar': { display: 'none' },
            msOverflowStyle: 'none',
            scrollbarWidth: 'none'
          }}
        >
          {categories.length > 0 ? (
            categories.map(renderCategoryItem)
          ) : (
            // Show placeholder categories while loading
            Array.from({ length: 8 }, (_, index) => (
              <Box
                key={`placeholder-${index}`}
                sx={{
                  minWidth: 100,
                  maxWidth: 100,
                  textAlign: 'center',
                  p: 1,
                }}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    mx: 'auto',
                    mb: 1,
                    backgroundColor: '#f0f0f0',
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    animation: 'pulse 1.5s ease-in-out infinite alternate',
                    '@keyframes pulse': {
                      from: { opacity: 0.6 },
                      to: { opacity: 1 }
                    }
                  }}
                >
                  <CategoryIcon sx={{ fontSize: 40, color: '#ccc' }} />
                </Box>
                <Box
                  sx={{
                    height: '2.4em',
                    backgroundColor: '#f0f0f0',
                    borderRadius: 1,
                    animation: 'pulse 1.5s ease-in-out infinite alternate',
                  }}
                />
              </Box>
            ))
          )}
        </Box>
      </Box>

      {/* Banners Section */}
      {!bannersLoading && banners.length > 0 && (
        <Box sx={{ backgroundColor: 'white', mb: 2, position: 'relative' }}>
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              paddingTop: '31.58%', // 600/1900 = 0.3158 for 1900x600 aspect ratio
              overflow: 'hidden',
              cursor: banners[currentBannerIndex]?.link ? 'pointer' : 'default'
            }}
            onClick={() => handleBannerClick(banners[currentBannerIndex]?.link)}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%'
              }}
            >
              <FastImage
                src={banners[currentBannerIndex]?.image}
                alt={banners[currentBannerIndex]?.name}
                priority={true}
                aspectRatio="3.17"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain'
                }}
              />
            </Box>

            {/* Navigation Arrows */}
            {banners.length > 1 && (
              <>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    prevBanner();
                  }}
                  sx={{
                    position: 'absolute',
                    left: 10,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.95)'
                    },
                    zIndex: 2
                  }}
                >
                  <ArrowForwardIosIcon />
                </IconButton>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    nextBanner();
                  }}
                  sx={{
                    position: 'absolute',
                    right: 10,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.95)'
                    },
                    zIndex: 2
                  }}
                >
                  <ArrowBackIosIcon />
                </IconButton>

                {/* Banner Indicators */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 15,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    gap: 1,
                    zIndex: 2
                  }}
                >
                  {banners.map((_, index) => (
                    <Box
                      key={index}
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentBannerIndex(index);
                      }}
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: currentBannerIndex === index ? 'white' : 'rgba(255, 255, 255, 0.5)',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        border: '1px solid rgba(0, 0, 0, 0.2)',
                        '&:hover': {
                          backgroundColor: 'white',
                          transform: 'scale(1.2)'
                        }
                      }}
                    />
                  ))}
                </Box>
              </>
            )}
          </Box>
        </Box>
      )}

      {/* Offers Section */}
      <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #eee' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, px: 2, pt: 2 }}>
          <Typography variant="h6" sx={{ 
            fontWeight: 'bold', 
            color: '#ee5a52',
            background: 'linear-gradient(45deg, #ff6b6b, #ee5a52)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            🔥 عروض مميزة {offers.length > 0 && `(${offers.length} عروض)`}
          </Typography>
        </Box>

        {offersLoading ? (
          <Box sx={{ textAlign: 'center', py: 2, px: 2, color: 'text.secondary', fontSize: '0.9rem' }}>
            جاري تحميل العروض...
          </Box>
        ) : offers.length > 0 ? (
          <Box
            id="offers-container"
            onMouseDown={handleMouseDown}
            sx={{
              display: 'flex',
              gap: 2,
              overflowX: 'auto',
              pb: 2,
              px: 2,
              scrollBehavior: 'smooth',
              cursor: 'grab',
              userSelect: 'none',
              '&:active': {
                cursor: 'grabbing'
              },
              '&::-webkit-scrollbar': { display: 'none' },
              msOverflowStyle: 'none',
              scrollbarWidth: 'none'
            }}
          >
            {offers.map((product, index) => renderOfferCard(product, index, 0))}
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 2, px: 2, color: 'text.secondary', fontSize: '0.9rem' }}>
            لا توجد عروض متاحة حالياً
          </Box>
        )}
      </Box>

      {/* Products Preview Section */}
      <Box sx={{ p: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#3b5dac', mb: 3, textAlign: 'center' }}>
          منتجات مختارة لك
        </Typography>

        <Box>
          {Object.keys(productsByBrand).length > 0 ? (
            Object.entries(productsByBrand)
              .sort(([,a], [,b]) => {
                // Filter active products for sorting
                const activeA = a.products.filter(product => 
                  product.active === 1 || product.active === true || product.status === 'active'
                );
                const activeB = b.products.filter(product => 
                  product.active === 1 || product.active === true || product.status === 'active'
                );
                return activeB.length - activeA.length;
              })
              .map(([brandId, brandData], brandIndex) => {
                // Filter active products for display and limit to 8
                const activeProducts = brandData.products
                  .filter(product => 
                    product.active === 1 || product.active === true || product.status === 'active'
                  )
                  .slice(0, 8); // Limit to 8 products per brand strip
                
                if (activeProducts.length === 0) {
                  // Don't show brands with no active products
                  return null;
                }
                return renderBrandStrip(brandId, { ...brandData, products: activeProducts }, brandIndex);
              }).filter(Boolean) // Remove null entries
          ) : (
            // Show placeholder brand strips while loading
            Array.from({ length: 3 }, (_, brandIndex) => (
              <Box key={`brand-placeholder-${brandIndex}`} sx={{ mb: 4 }}>
                {/* Brand Header Placeholder */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, px: 2 }}>
                  <Box
                    sx={{
                      width: 150,
                      height: 28,
                      backgroundColor: '#f0f0f0',
                      borderRadius: 1,
                      animation: 'pulse 1.5s ease-in-out infinite alternate',
                    }}
                  />
                </Box>

                {/* Products Strip Placeholder */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    overflowX: 'auto',
                    pb: 2,
                    px: 2,
                    '&::-webkit-scrollbar': { display: 'none' },
                    msOverflowStyle: 'none',
                    scrollbarWidth: 'none'
                  }}
                >
                  {Array.from({ length: 8 }, (_, productIndex) => (
                    <Box
                      key={`product-placeholder-${brandIndex}-${productIndex}`}
                      sx={{
                        minWidth: 150,
                        maxWidth: 150,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        mr: 2,
                      }}
                    >
                      {/* Image placeholder */}
                      <Box
                        sx={{
                          width: '100%',
                          height: 150,
                          backgroundColor: '#f0f0f0',
                          borderRadius: 1,
                          mb: 1,
                          animation: 'pulse 1.5s ease-in-out infinite alternate',
                        }}
                      />
                      {/* Text placeholders */}
                      <Box sx={{ width: '100%', textAlign: 'center', px: 1 }}>
                        <Box
                          sx={{
                            height: '2.2em',
                            backgroundColor: '#f0f0f0',
                            borderRadius: 1,
                            mb: 0.5,
                            animation: 'pulse 1.5s ease-in-out infinite alternate',
                          }}
                        />
                        <Box
                          sx={{
                            height: '1.5em',
                            backgroundColor: '#f0f0f0',
                            borderRadius: 1,
                            width: '60%',
                            mx: 'auto',
                            animation: 'pulse 1.5s ease-in-out infinite alternate',
                          }}
                        />
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))
          )}
        </Box>

        {/* View All Products Button */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/store')}
            sx={{
              backgroundColor: '#3b5dac',
              color: 'white',
              px: 4,
              py: 1.5,
              borderRadius: 2,
              fontSize: '1.1rem',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: '#2a406e'
              }
            }}
          >
            عرض جميع المنتجات
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default Home;